import { useState, useEffect } from "react"
import { Formik, Form, Field, ErrorMessage } from "formik"
import * as Yup from "yup"
import { useSearchParams, Link } from "react-router-dom"
import Layout from '../components/Layout';
import { api, getAuthHeaders } from '../utils/api';
import "../styles/collaborators.css";
import { Share2 } from "lucide-react";

// Mock data for demonstration
const allCollaborators = [
  {
    id: 1,
    firstName: "John",
    lastName: "Doe",
    email: "john-doe@gmail.com",
    relation: "Brother",
    type: "partial-access",
    assetCount: 6,
    liabilityCount: 2,
    mobileNumber: "9759391234",
    countryCode: "+91",
    sharedAssets: [1, 3, 5]
  },
  {
    id: 2,
    firstName: "Sarah",
    lastName: "Johnson",
    email: "sarah.johnson@gmail.com",
    relation: "Sister",
    type: "partial-access",
    assetCount: 4,
    liabilityCount: 1,
    mobileNumber: "9759395678",
    countryCode: "+91",
    sharedAssets: [2, 4]
  },
  {
    id: 3,
    firstName: "Emily",
    lastName: "Davis",
    email: "emily.davis@gmail.com",
    relation: "Spouse",
    type: "legacy-access",
    assetCount: 0,
    liabilityCount: 0,
    mobileNumber: "9759399012",
    countryCode: "+91",
    inactiveDays: 15,
    gracePeriodDays: 7
  }
];

const mockAssets = [
  { id: 1, name: "Property - Main House", type: "Physical" },
  { id: 2, name: "Stock Portfolio", type: "Digital" },
  { id: 3, name: "Savings Account", type: "Digital" },
  { id: 4, name: "Vehicle - Tesla", type: "Physical" },
  { id: 5, name: "Bitcoin Wallet", type: "Digital" },
];

const Collaborators = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeFilter, setActiveFilter] = useState(tabParam === 'legacy-access' ? 'legacy-access' : "partial-access");
  const [searchTerm, setSearchTerm] = useState("");
  // Remove old collaborators state, use only legacyCollaborators and partialCollaborators
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCollaborator, setEditingCollaborator] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState([]);

  // Legacy Access modal state
  const [showLegacyEditModal, setShowLegacyEditModal] = useState(false);
  const [legacyEditData, setLegacyEditData] = useState({ inactivityDays: '', gracePeriodDays: '', notes: '' });
  const [legacyEditLoading, setLegacyEditLoading] = useState(false);
  const [legacyEditError, setLegacyEditError] = useState('');
  const [legacyCollaborators, setLegacyCollaborators] = useState([]);
  const [partialCollaborators, setPartialCollaborators] = useState([]);
  const [legacyaccesslength, setLegacyaccesslength] = useState('');
  const [partialaccesslength, setPartialaccesslength] = useState("0");

  // Shared items modal state
  const [showSharedItemsModal, setShowSharedItemsModal] = useState(false);
  const [selectedCollaboratorForShared, setSelectedCollaboratorForShared] = useState(null);
  const [sharedItemsData, setSharedItemsData] = useState({ assets: [], liabilities: [] });
  const [sharedItemsLoading, setSharedItemsLoading] = useState(false);
  const [activeSharedTab, setActiveSharedTab] = useState('assets');
  
  // Store all shared items for counting
  const [allSharedAssets, setAllSharedAssets] = useState([]);
  const [allSharedLiabilities, setAllSharedLiabilities] = useState([]);

  const collaborators = activeFilter === 'legacy-access' ? legacyCollaborators : partialCollaborators;

  const filteredCollaborators = collaborators.filter(collaborator => {
    const matchesSearch = searchTerm === "" || 
      (collaborator.firstName && collaborator.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (collaborator.lastName && collaborator.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (collaborator.name && collaborator.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (collaborator.email && collaborator.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (collaborator.relation && collaborator.relation.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  // const legacyAccessCount = collaborators.filter(c => c.type === "legacy-access").length;
  const partialAccessCount = collaborators.filter(c => c.type === "partial-access").length;

  const handleEditCollaborator = (collaborator) => {
    setEditingCollaborator(collaborator);
    setSelectedAssets(collaborator.sharedAssets || []);
    setShowEditModal(true);
  };

  // Fetch shared items for a collaborator
  const fetchSharedItems = async (collaboratorId) => {
    setSharedItemsLoading(true);
    const accessToken = localStorage.getItem('access_token');
    try {
      // Fetch assets shared by me
      const assetsResponse = await api.get('/sharing/assets/shared-by-me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      // Fetch liabilities shared by me (similar pattern)
      const liabilitiesResponse = await api.get('/sharing/liabilities/shared-by-me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      }).catch(() => ({ data: [] }));
      
      // Process assets - filter by collaborator ID
      let sharedAssets = [];
      const assetsData = assetsResponse.data;
      if (Array.isArray(assetsData?.results)) {
        // Filter by collaborator ID
        sharedAssets = assetsData.results
          .filter(item => String(item.collaboratorId?.id) === String(collaboratorId))
          .map(item => ({
            id: item.assetId?.id,
            name: item.assetId?.name || item.assetId?.assetName || 'Asset',
            type: item.assetId?.assetType || item.assetId?.type || 'General',
            value: item.assetId?.value
          }));
      } else if (Array.isArray(assetsData)) {
        sharedAssets = assetsData
          .filter(item => String(item.collaboratorId?.id) === String(collaboratorId))
          .map(item => ({
            id: item.assetId?.id,
            name: item.assetId?.name || item.assetId?.assetName || 'Asset',
            type: item.assetId?.assetType || item.assetId?.type || 'General',
            value: item.assetId?.value
          }));
      }
      
      // Process liabilities - filter by collaborator ID
      let sharedLiabilities = [];
      const liabilitiesData = liabilitiesResponse.data;
      if (Array.isArray(liabilitiesData?.results)) {
        sharedLiabilities = liabilitiesData.results
          .filter(item => String(item.collaboratorId?.id) === String(collaboratorId))
          .map(item => ({
            id: item.liabilityId?.id,
            name: item.liabilityId?.name || item.liabilityId?.liabilityName || 'Liability',
            type: item.liabilityId?.liabilityType || item.liabilityId?.type || 'General',
            amount: item.liabilityId?.amount
          }));
      } else if (Array.isArray(liabilitiesData)) {
        sharedLiabilities = liabilitiesData
          .filter(item => String(item.collaboratorId?.id) === String(collaboratorId))
          .map(item => ({
            id: item.liabilityId?.id,
            name: item.liabilityId?.name || item.liabilityId?.liabilityName || 'Liability',
            type: item.liabilityId?.liabilityType || item.liabilityId?.type || 'General',
            amount: item.liabilityId?.amount
          }));
      }
      
      setSharedItemsData({
        assets: sharedAssets,
        liabilities: sharedLiabilities
      });
    } catch (error) {
      console.error('Error fetching shared items:', error);
      // Set empty data on error - UI will show empty state
      setSharedItemsData({ assets: [], liabilities: [] });
    } finally {
      setSharedItemsLoading(false);
    }
  };

  // Handle collaborator name click
  const handleCollaboratorClick = (collaborator) => {
    setSelectedCollaboratorForShared(collaborator);
    fetchSharedItems(collaborator.id);
    setShowSharedItemsModal(true);
  };

  const handleRemoveCollaborator = (id) => {
      const accessToken = localStorage.getItem('access_token');
      // Find collaborator in partialCollaborators
      const partialCollaborator = partialCollaborators.find(c => c.id === id);
      if (partialCollaborator && partialCollaborator.type === "partial-access") {
        // Call API to delete partial access collaborator
        api.delete(`/sharing/collaborators/${id}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((res) => {
          if (res.status === 200 || res.status === 204) {
            setPartialCollaborators(prev => prev.filter(c => c.id !== id));
            setPartialaccesslength(prev => String(Math.max(0, Number(prev) - 1)));
          } else {
            // alert("Failed to remove collaborator.");
          }
        }).catch(() => {
          // alert("Failed to remove collaborator.");
        });
      } else {
        // Handle legacy access collaborator removal
        const legacyCollaborator = legacyCollaborators.find(c => c.id === id);
        if (legacyCollaborator && legacyCollaborator.type === "legacy-access") {
          // Call API to delete legacy access
          api.delete('/sharing/legacy', {
            headers: { Authorization: `Bearer ${accessToken}` },
          }).then((res) => {
            if (res.status === 200 || res.status === 204) {
              setLegacyCollaborators(prev => prev.filter(c => c.id !== id));
              setLegacyaccesslength(prev => String(Math.max(0, Number(prev) - 1)));
            } else {
              // alert("Failed to remove legacy collaborator.");
            }
          }).catch(() => {
            // alert("Failed to remove legacy collaborator.");
          });
        }
      }

  };

  // Validation schemas
  const partialAccessSchema = Yup.object().shape({
    firstName: Yup.string().min(2, "First name must be at least 2 characters").required("First name is required"),
    lastName: Yup.string().min(2, "Last name must be at least 2 characters").required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobileNumber: Yup.string().matches(/^[0-9]{10}$/, "Mobile number must be 10 digits").required("Mobile number is required"),
    relation: Yup.string().required("Relation is required"),
  });

  const legacyAccessSchema = Yup.object().shape({
    firstName: Yup.string().min(2, "First name must be at least 2 characters").required("First name is required"),
    lastName: Yup.string().min(2, "Last name must be at least 2 characters").required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobileNumber: Yup.string().matches(/^[0-9]{10}$/, "Mobile number must be 10 digits").required("Mobile number is required"),
    inactiveDays: Yup.number().min(1, "Must be at least 1 day").required("Inactivity days is required"),
    gracePeriodDays: Yup.number().min(1, "Must be at least 1 day").required("Grace period is required"),
  });
    // Tab-based API states
    const [legacyList, setLegacyList] = useState([]);
    const [partialList, setPartialList] = useState([]);
    const [legacyLoading, setLegacyLoading] = useState(false);
    const [partialLoading, setPartialLoading] = useState(false);
    const [legacyError, setLegacyError] = useState("");
    const [partialError, setPartialError] = useState("");
  const handleSubmitEdit = (values) => {
    const updatedData = {
      ...values,
      sharedAssets: selectedAssets,
      assetCount: selectedAssets.length,
    };
    
    // setCollaborators(prev => 
    //   prev.map(c => c.id === editingCollaborator.id ? { ...c, ...updatedData } : c)
    // );
    
    setShowEditModal(false);
    setEditingCollaborator(null);
    setSelectedAssets([]);
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const EditPartialAccessModal = () => (
    <div className="edit-modal-overlay" onClick={() => setShowEditModal(false)}>
      <div className="edit-modal-container" onClick={(e) => e.stopPropagation()}>
        <button className="edit-modal-close-btn" onClick={() => setShowEditModal(false)}>
          <i className="bi bi-x"></i>
        </button>
        <div className="edit-modal-header">
          <h2 className="edit-modal-title">Edit Collaborator (Partial Access)</h2>
          <p className="edit-modal-subtitle">
            Update collaborator details and manage shared assets
          </p>
        </div>
        <Formik
          initialValues={{
            firstName: editingCollaborator.firstName,
            lastName: editingCollaborator.lastName,
            email: editingCollaborator.email,
            mobileNumber: editingCollaborator.mobileNumber,
            relation: editingCollaborator.relation,
          }}
          validationSchema={partialAccessSchema}
          onSubmit={handleSubmitEdit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="edit-form">
              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="firstName">First Name*</label>
                  <Field
                    type="text"
                    id="firstName"
                    name="firstName"
                    className={`form-control ${errors.firstName && touched.firstName ? 'is-invalid' : ''}`}
                    placeholder="John"
                  />
                  <ErrorMessage name="firstName" component="div" className="error-text" />
                </div>
                <div className="edit-form-group">
                  <label htmlFor="lastName">Last Name*</label>
                  <Field
                    type="text"
                    id="lastName"
                    name="lastName"
                    className={`form-control ${errors.lastName && touched.lastName ? 'is-invalid' : ''}`}
                    placeholder="Doe"
                  />
                  <ErrorMessage name="lastName" component="div" className="error-text" />
                </div>
              </div>
              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="email">Email*</label>
                  <Field
                    type="email"
                    id="email"
                    name="email"
                    className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                    placeholder="john.doe@gmail.com"
                  />
                  <ErrorMessage name="email" component="div" className="error-text" />
                </div>
                <div className="edit-form-group">
                  <label htmlFor="mobileNumber">Mobile Number*</label>
                  <Field
                    type="tel"
                    id="mobileNumber"
                    name="mobileNumber"
                    className={`form-control ${errors.mobileNumber && touched.mobileNumber ? 'is-invalid' : ''}`}
                    placeholder="9759391234"
                    maxLength="10"
                  />
                  <ErrorMessage name="mobileNumber" component="div" className="error-text" />
                </div>
              </div>
              <div className="edit-form-group">
                <label htmlFor="relation">Relation*</label>
                <Field as="select" id="relation" name="relation" className="form-control">
                  {["Brother", "Sister", "Father", "Mother", "Spouse", "Friend", "Other"].map((rel) => (
                    <option key={rel} value={rel}>{rel}</option>
                  ))}
                </Field>
                <ErrorMessage name="relation" component="div" className="error-text" />
              </div>
              <div className="edit-assets-section">
                <h6 className="edit-assets-title">Shared Assets</h6>
                <div className="edit-assets-list">
                  {mockAssets.map((asset) => (
                    <label key={asset.id} className="edit-asset-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedAssets.includes(asset.id)}
                        onChange={() => toggleAssetSelection(asset.id)}
                        className="edit-asset-checkbox"
                      />
                      <div className="edit-asset-info">
                        <span className="edit-asset-name">{asset.name}</span>
                        <span className={`edit-asset-type ${asset.type.toLowerCase()}`}>{asset.type}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="edit-form-actions">
                <button type="button" className="edit-btn-cancel" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="edit-btn-submit" disabled={isSubmitting}>
                  Save Changes
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );

  const EditLegacyAccessModal = () => (
    <div className="edit-modal-overlay" onClick={() => setShowEditModal(false)}>
      <div className="edit-modal-container legacy" onClick={(e) => e.stopPropagation()}>
        <button className="edit-modal-close-btn" onClick={() => setShowEditModal(false)}>
          <i className="bi bi-x"></i>
        </button>

        <div className="edit-modal-header">
          <h2 className="edit-modal-title">Edit Collaborator (Legacy Access)</h2>
          <p className="edit-modal-subtitle">
            Update collaborator details and legacy access settings
          </p>
        </div>

        <Formik
          initialValues={{
            firstName: editingCollaborator.firstName,
            lastName: editingCollaborator.lastName,
            email: editingCollaborator.email,
            mobileNumber: editingCollaborator.mobileNumber,
            inactiveDays: editingCollaborator.inactiveDays || 15,
            gracePeriodDays: editingCollaborator.gracePeriodDays || 7,
          }}
          validationSchema={legacyAccessSchema}
          onSubmit={handleSubmitEdit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="edit-form">
              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="firstName">First Name*</label>
                  <Field
                    type="text"
                    id="firstName"
                    name="firstName"
                    className={`form-control ${errors.firstName && touched.firstName ? 'is-invalid' : ''}`}
                    placeholder="John"
                  />
                  <ErrorMessage name="firstName" component="div" className="error-text" />
                </div>

                <div className="edit-form-group">
                  <label htmlFor="lastName">Last Name*</label>
                  <Field
                    type="text"
                    id="lastName"
                    name="lastName"
                    className={`form-control ${errors.lastName && touched.lastName ? 'is-invalid' : ''}`}
                    placeholder="Doe"
                  />
                  <ErrorMessage name="lastName" component="div" className="error-text" />
                </div>
              </div>

              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="email">Email*</label>
                  <Field
                    type="email"
                    id="email"
                    name="email"
                    className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                    placeholder="john.doe@gmail.com"
                  />
                  <ErrorMessage name="email" component="div" className="error-text" />
                </div>

                <div className="edit-form-group">
                  <label htmlFor="mobileNumber">Mobile Number*</label>
                  <Field
                    type="tel"
                    id="mobileNumber"
                    name="mobileNumber"
                    className={`form-control ${errors.mobileNumber && touched.mobileNumber ? 'is-invalid' : ''}`}
                    placeholder="9759391234"
                    maxLength="10"
                  />
                  <ErrorMessage name="mobileNumber" component="div" className="error-text" />
                </div>
              </div>

              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="inactiveDays">Inactivity Days*</label>
                  <Field as="select" id="inactiveDays" name="inactiveDays" className="form-control">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day} {day === 1 ? 'day' : 'days'}</option>
                    ))}
                  </Field>
                  <ErrorMessage name="inactiveDays" component="div" className="error-text" />
                </div>

                <div className="edit-form-group">
                  <label htmlFor="gracePeriodDays">Grace Period Days*</label>
                  <Field as="select" id="gracePeriodDays" name="gracePeriodDays" className="form-control">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day} {day === 1 ? 'day' : 'days'}</option>
                    ))}
                  </Field>
                  <ErrorMessage name="gracePeriodDays" component="div" className="error-text" />
                </div>
              </div>

              <div className="edit-info-box">
                <div className="edit-info-icon">i</div>
                <p className="edit-info-text">
                  After the specified inactivity period, the system will wait for the grace period before granting full legacy access to this collaborator.
                </p>
              </div>

              <div className="edit-form-actions">
                <button type="button" className="edit-btn-cancel" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="edit-btn-submit" disabled={isSubmitting}>
                  Save Changes
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );

  // Fetch collaborators on mount
useEffect(() => {
  const accessToken = localStorage.getItem('access_token');
  // Legacy Access
  api.get('/sharing/legacy', {
    headers: { Authorization: `Bearer ${accessToken}` },
  }).then((res) => {
    const owner = res.data.contactInfo;
    setLegacyaccesslength(owner ? '1' : '0');
    if (owner) {
      setLegacyCollaborators([{ ...owner, type: 'legacy-access' }]);
    } else {
      setLegacyCollaborators([]);
    }
  }).catch((err) => {
    if (err?.response?.status === 404) {
      setLegacyCollaborators([]);
      setLegacyaccesslength('0');
    } else {
      // Optionally handle other errors
    }
  });

  // Partial Access
api.get('/sharing/collaborators?status=active&limit=20&page=1', {
  headers: { Authorization: `Bearer ${accessToken}` },
}).then((res) => {
  if (Array.isArray(res.data.results)) {
    // Fetch all shared assets and liabilities for counting
    Promise.all([
      api.get('/sharing/assets/shared-by-me', { headers: { Authorization: `Bearer ${accessToken}` } }),
      api.get('/sharing/liabilities/shared-by-me', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => ({ data: [] }))
    ]).then(([assetsRes, liabilitiesRes]) => {
      const allAssets = assetsRes.data?.results || assetsRes.data || [];
      const allLiabilities = liabilitiesRes.data?.results || liabilitiesRes.data || [];
      setAllSharedAssets(allAssets);
      setAllSharedLiabilities(allLiabilities);
      
      // Calculate counts for each collaborator
      const collaboratorsWithCounts = res.data.results.map(item => {
        const collabId = item.collaboratorId.id;
        const assetCount = allAssets.filter(a => String(a.collaboratorId?.id) === String(collabId)).length;
        const liabilityCount = allLiabilities.filter(l => String(l.collaboratorId?.id) === String(collabId)).length;
        return {
          id: item.collaboratorId.id,
          name: item.collaboratorId?.name || `${item.collaboratorId?.firstName || ''} ${item.collaboratorId?.lastName || ''}`.trim(),
          firstName: item.collaboratorId?.firstName || '',
          lastName: item.collaboratorId?.lastName || '',
          email: item.collaboratorId?.email || "",
          mobileNumber: item.collaboratorId?.mobileNumber || "",
          relation: item.collaboratorId?.relation || "",
          type: "partial-access",
          status: item.status,
          permissions: item.permissions,
          assetCount: assetCount,
          liabilityCount: liabilityCount,
          invitedAt: item.invitedAt,
          createdAt: item.createdAt,
        };
      });
      
      setPartialCollaborators(collaboratorsWithCounts);
      setPartialaccesslength(res.data.total || "0");
    });
  } else {
    setPartialCollaborators([]);
  }
});
}, []);

  // Edit handler
  const handleLegacyEditClick = (legacyItem) => {
    setLegacyEditData({
      inactivityDays: legacyItem?.inactivityDays || '',
      gracePeriodDays: legacyItem?.gracePeriodDays || '',
      notes: legacyItem?.notes || '',
      id: legacyItem?.id || '',
    });
    setShowLegacyEditModal(true);
  };

  // Update handler
  const handleLegacyEditSave = async () => {
    setLegacyEditLoading(true);
    setLegacyEditError('');
    const accessToken = localStorage.getItem('access_token');
    try {
      const payload = {
        inactivityDays: Number(legacyEditData.inactivityDays),
        gracePeriodDays: Number(legacyEditData.gracePeriodDays),
        notes: legacyEditData.notes,
      };
      const res = await api.put('/v1/sharing/legacy', payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.status === 200) {
        setShowLegacyEditModal(false);
        // Refresh list
        api.get('/sharing/legacy', {
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((res) => {
          // setCollaborators(res.data.legacyUserId || []);
        });
      } else {
        setLegacyEditError('Failed to update legacy access.');
      }
    } catch {
      setLegacyEditError('Failed to update legacy access.');
    } finally {
      setLegacyEditLoading(false);
    }
  };

  // Delete handler
  const handleLegacyDelete = async (legacyItem) => {
    const accessToken = localStorage.getItem('access_token');
    try {
      await api.delete(`/v1/sharing/legacy/${legacyItem.id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // Refresh list
      api.get('/sharing/legacy', {
        headers: { Authorization: `Bearer ${accessToken}` },
      }).then((res) => {
        // setCollaborators(res.data.legacyUserId || []);
      });
    } catch {}
  };

  // Shared Items Modal Component
  const SharedItemsModal = () => {
    if (!showSharedItemsModal || !selectedCollaboratorForShared) return null;

    return (
      <div className="shared-items-modal-overlay" onClick={() => setShowSharedItemsModal(false)}>
        <div className="shared-items-modal-container" onClick={(e) => e.stopPropagation()}>
          <button className="shared-items-modal-close-btn" onClick={() => setShowSharedItemsModal(false)}>
            <i className="bi bi-x"></i>
          </button>
          
          <div className="shared-items-modal-header">
            <div className="shared-items-collaborator-info">
              <img
                src={`https://ui-avatars.com/api/?name=${selectedCollaboratorForShared.name}&background=random&size=48`}
                alt="Avatar"
                className="shared-items-avatar"
              />
              <div>
                <h2 className="shared-items-modal-title">
                  {selectedCollaboratorForShared.name}
                </h2>
                <p className="shared-items-modal-subtitle">
                  Shared Items
                </p>
              </div>
            </div>
          </div>

          <div className="shared-items-tabs">
            <button
              className={`shared-items-tab ${activeSharedTab === 'assets' ? 'active' : ''}`}
              onClick={() => setActiveSharedTab('assets')}
            >
              <i className="bi bi-building"></i>
              Assets <span className="tab-count">{sharedItemsData.assets?.length || 0}</span>
            </button>
            <button
              className={`shared-items-tab ${activeSharedTab === 'liabilities' ? 'active' : ''}`}
              onClick={() => setActiveSharedTab('liabilities')}
            >
              <i className="bi bi-credit-card"></i>
              Liabilities <span className="tab-count">{sharedItemsData.liabilities?.length || 0}</span>
            </button>
          </div>

          <div className="shared-items-content">
            {sharedItemsLoading ? (
              <div className="shared-items-loading">
                <div className="shared-items-spinner"></div>
                <p>Loading shared items...</p>
              </div>
            ) : activeSharedTab === 'assets' ? (
              sharedItemsData.assets?.length > 0 ? (
                <div className="shared-items-list">
                  {sharedItemsData.assets.map((asset, index) => (
                    <div key={index} className="shared-item-card">
                      <div className="shared-item-icon">
                        <i className="bi bi-building"></i>
                      </div>
                      <div className="shared-item-details">
                        <h4 className="shared-item-name">{asset.name || asset.assetName || 'Asset'}</h4>
                        <p className="shared-item-type">{asset.type || asset.assetType || 'General'}</p>
                        {asset.value && (
                          <p className="shared-item-value">{asset.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="shared-items-empty">
                  <i className="bi bi-folder2-open"></i>
                  <p>No assets shared with this collaborator</p>
                </div>
              )
            ) : sharedItemsData.liabilities?.length > 0 ? (
              <div className="shared-items-list">
                {sharedItemsData.liabilities.map((liability, index) => (
                  <div key={index} className="shared-item-card">
                    <div className="shared-item-icon liability">
                      <i className="bi bi-credit-card"></i>
                    </div>
                    <div className="shared-item-details">
                      <h4 className="shared-item-name">{liability.name || liability.liabilityName || 'Liability'}</h4>
                      <p className="shared-item-type">{liability.type || liability.liabilityType || 'General'}</p>
                      {liability.amount && (
                        <p className="shared-item-value">{liability.amount}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="shared-items-empty">
                <i className="bi bi-folder2-open"></i>
                <p>No liabilities shared with this collaborator</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
    <div className="container-fluid">
      <div className="collaborators-wrapper">
        <div className="row">
          <div className="col-12">
            <div className="filter-bar">
              <div className="filter-left">
                <div className="items-filters">
                  <button
                    className={`filter-btn ${activeFilter === "partial-access" ? "active" : ""}`}
                    onClick={() => setActiveFilter("partial-access")}
                  >
                    Partial Access <span className="filter-count">{partialaccesslength}</span>
                  </button>
                  <button
                    className={`filter-btn ${activeFilter === "legacy-access" ? "active" : ""}`}
                    onClick={() => setActiveFilter("legacy-access")}
                  >
                    Legacy Access <span className="filter-count">{legacyaccesslength}</span>
                  </button>
                </div>
              </div>

              <div className="filter-right">
                <div className="search-container">
                  <i className="bi bi-search search-icon"></i>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
            {/* Explain the two access types and link to where each is set up (ML-38). Links only; no new sharing logic. */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
              <p className="text-muted small mb-0">
                {activeFilter === "partial-access"
                  ? "Partial access lets someone view only the assets and liabilities you share with them."
                  : "Legacy access gives a trusted person access to your records after a period of inactivity you choose, plus a grace period."}
              </p>
              <Link
                to={activeFilter === "partial-access" ? "/my-assets" : "/editprofile?tab=legacyAccessSetting"}
                className="btn btn-primary btn-sm"
              >
                <i className="bi bi-person-plus me-1"></i>
                {activeFilter === "partial-access" ? "Share assets" : "Add legacy contact"}
              </Link>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12 d-flex flex-wrap gap-3">
            {filteredCollaborators.length === 0 ? (
              <div className="no-collaborator">
                <div className="no-collaborator-icon">!</div>
                <h6>No Collaborator Found!</h6>
                <p>
                  {searchTerm 
                    ? "No collaborators match your search criteria" 
                    : `No collaborators with ${activeFilter === "legacy-access" ? "legacy" : "partial"} access`}
                </p>
              </div>
            ) : (
              filteredCollaborators.map((collaborator) => (
                <div key={collaborator.id} className="collaborator-card">
                  <div className="me-3 flex-shrink-0" style={{ width: 48, height: 48 }}>
                    <img
                      src={`https://ui-avatars.com/api/?name=${collaborator.name}&background=random&size=48`}
                      alt="Avatar"
                      className="avatar-img"
                      width={48}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <h6 
                          className="collaborator-name"
                          style={{ fontWeight: 700, color: '#23255A', marginBottom: 0 }}
                        >
                          {collaborator.name}
                        </h6>
                      </div>
                      <div className="dropdown">
                        <button 
                          className="btn btn-sm dropdown-btn" 
                          type="button" 
                          onClick={(e) => {
                            const menu = e.currentTarget.nextElementSibling;
                            document.querySelectorAll('.dropdown-menu').forEach(m => {
                              if (m !== menu) m.classList.remove('show');
                            });
                            menu?.classList.toggle('show');
                          }}
                        >
                          ⋮
                        </button>
                        <ul className="dropdown-menu">
                          {/* <li>
                            <button 
                              className="dropdown-item d-flex align-items-center"
                              onClick={() => {
                                handleEditCollaborator(collaborator);
                                document.querySelectorAll('.dropdown-menu').forEach(m => m.classList.remove('show'));
                              }}
                            >
                              <span className="me-2">✎</span> Edit
                            </button>
                          </li> */}
                          <li>
                            <button
                              className="dropdown-item d-flex align-items-center"
                              onClick={() => {
                                handleCollaboratorClick(collaborator);
                                document.querySelectorAll('.dropdown-menu').forEach(m => m.classList.remove('show'));
                              }}
                            >
                              <span className="me-2"><Share2 width={14} height={14}/> </span> Shared Data
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item d-flex align-items-center text-danger"
                              onClick={() => {
                                handleRemoveCollaborator(collaborator.id);
                                document.querySelectorAll('.dropdown-menu').forEach(m => m.classList.remove('show'));
                              }}
                            >
                              <span className="me-2">✕</span> Remove
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <p className="card-text mb-0" style={{ color: '#6B6E9C', fontWeight: 500 }}>
                      {collaborator.email}
                    </p>
                    <div className="d-flex align-items-center gap-4 mt-2 text-sm">
                      <div>Assets <span className="badge bg-blue-50 text-dark fw-bold">{collaborator.assetCount || 0}</span></div>
                      <div>Liabilities <span className="badge bg-purple-50 text-dark fw-bold">{collaborator.liabilityCount || 0}</span></div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {showEditModal && editingCollaborator && (
          editingCollaborator.type === "partial-access" ? (
            <EditPartialAccessModal />
          ) : (
            <EditLegacyAccessModal />
          )
        )}

        {/* Legacy Access Edit Modal */}
        {showLegacyEditModal && (
          <div className="modal-overlay" style={{position: 'fixed', top:0, left:0, right:0, bottom:0, background: 'rgba(0,0,0,0.3)', zIndex: 9999}}>
            <div className="modal-content" style={{background: '#fff', padding: 24, borderRadius: 8, maxWidth: 500, margin: '80px auto', boxShadow: '0 2px 16px rgba(0,0,0,0.15)'}}>
              <h2>Edit Legacy Access</h2>
              <div className="edit-form-group mb-4">
                <label>Inactivity Days</label>
                <select
                  value={legacyEditData.inactivityDays}
                  onChange={e => setLegacyEditData({ ...legacyEditData, inactivityDays: e.target.value })}
                  style={{width:'100%', marginTop:4}}
                  className="form-select"
                >
                  <option value="">Select</option>
                  {Array.from({ length: 31 }, (_, i) => (
                    <option key={i+1} value={i+1}>{i+1}</option>
                  ))}
                </select>
              </div>
              <div className="edit-form-group mb-4">
                <label>Grace Period Days</label>
                <select
                  value={legacyEditData.gracePeriodDays}
                  onChange={e => setLegacyEditData({ ...legacyEditData, gracePeriodDays: e.target.value })}
                  style={{width:'100%', marginTop:4}}
                  className="form-select"
                >
                  <option value="">Select</option>
                  {Array.from({ length: 31 }, (_, i) => (
                    <option key={i+1} value={i+1}>{i+1}</option>
                  ))}
                </select>
              </div>
              <div className="edit-form-group mb-4">
                <label>Notes</label>
                <textarea
                  value={legacyEditData.notes}
                  onChange={e => setLegacyEditData({ ...legacyEditData, notes: e.target.value })}
                  style={{width:'100%', marginTop:4}}
                  className="form-control"
                />
              </div>
              {legacyEditError && <div className="error-message" style={{color:'red', marginBottom:8}}>{legacyEditError}</div>}
              <div className="modal-actions" style={{display:'flex', gap:12, marginTop:16}}>
                <button onClick={handleLegacyEditSave} disabled={legacyEditLoading} style={{padding:'8px 16px'}}>
                  {legacyEditLoading ? 'Saving...' : 'Save'}
                </button>
                <button onClick={() => setShowLegacyEditModal(false)} disabled={legacyEditLoading} style={{padding:'8px 16px'}}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Shared Items Modal */}
        <SharedItemsModal />
      </div>
    </div>
    </Layout>
  );
};

export default Collaborators;